import React, { useRef } from "react";
import { Button } from "./ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { glassIcon, kilnLogo } from "../helpers/logoHelpers";
import { glasstypes } from "../helpers/glassHelpers";
import * as yup from "yup";

import useFirebaseKilns from "../hooks/useFirebaseKilns";
import {
  HookInput as Input,
  HookSelect as Select,
  HookStars as Stars,
  HookTagInput,
} from "../helpers/formHelpers";

const ProjectForm = (props: any) => {
  const kilns = useFirebaseKilns();

  const schema = yup.object().shape({
    name: yup.string().required(),
    //width: yup.string().required(),
    //depth: yup.string().required(),
    thickness: yup.string().required(),
  });

  const initialValues = props.initialValues;
  const { control, handleSubmit, formState: { errors }, watch } = useForm({
    resolver: yupResolver(schema),
  });
  const currentUnit = (watch("length_unit", initialValues.length_unit) as any as string) || "mm";
  const glassType = watch("glass", initialValues.glass) as any;
  const kiln = watch("kiln") as any;

  const nameRef = useRef(null);
  const widthRef = useRef(null);
  const depthRef = useRef(null);
  const thicknessRef = useRef(null);
  const starsRef = useRef(null);
  const kilnRef = useRef(null);
  const glassRef = useRef(null);
  const notesRef = useRef(null);
  const lengthUnitRef = useRef(null);

  if (!kilns) {
    return <div>Loading...</div>;
  }

  const kiln_array = Object.values(kilns);

  const kiln_selections = kiln_array.map((kiln) => {
    return { label: kiln?.name || "", value: kiln?.id || "" };
  });
  return (
    <>
      <form onSubmit={handleSubmit(props.onSubmit)}>
        <div>
          <div className="border rounded-lg p-4">
            <Input
              control={control}
              errors={errors}
              defaultValue={initialValues.name}
              name="name"
              label="Project Name"
              fieldRef={nameRef}
              nextFieldRef={widthRef}
            />
            <div className="flex gap-4 items-end">
              <Input
                control={control}
                errors={errors}
                defaultValue={initialValues.width}
                name="width"
                label={`Width`}
                rightLabel={currentUnit}
                keyboardType="number-pad"
                fieldRef={widthRef}
                nextFieldRef={depthRef}
              />
              <Input
                control={control}
                errors={errors}
                defaultValue={initialValues.depth}
                name="depth"
                label={`Height`}
                rightLabel={currentUnit}
                keyboardType="number-pad"
                fieldRef={depthRef}
                nextFieldRef={thicknessRef}
              />
              <Input
                control={control}
                errors={errors}
                defaultValue={initialValues.thickness}
                name="thickness"
                label={`Thickness`}
                rightLabel={currentUnit}
                keyboardType="number-pad"
                fieldRef={thicknessRef}

                //nextFieldRef={kilnRef}
              />
            </div>
            <Stars
              control={control}
              errors={errors}
              name="stars"
              label="Stars"
              defaultValue={initialValues.stars}
              fieldRef={starsRef}
            />
            <Select
              control={control}
              errors={errors}
              name="length_unit"
              label="Sizes are in"
              defaultValue={initialValues.length_unit}
              items={[
                { label: "Millimetres", value: "mm" },
                { label: "Inches", value: "in" },
              ]}
              fieldRef={lengthUnitRef}
            />
            <div className="flex gap-4 items-end">
              <Select
                control={control}
                errors={errors}
                defaultValue={initialValues.kiln}
                name="kiln"
                label="Kiln"
                items={kiln_selections}
                fieldRef={kilnRef}
                labelStyle={{ width: "75px" }}
                selectStyle={{ width: "200px" }}
              />
              <span>
                <img className="inline-block h-7 w-7 rounded-full" src={kilnLogo((kilns as any)[kiln]?.manufacturer)} alt="" />
              </span>
            </div>

            <div className="flex gap-4 items-end">
              <Select
                control={control}
                errors={errors}
                defaultValue={initialValues.glass}
                name="glass"
                label="Glass Type"
                items={glasstypes}
                fieldRef={glassRef}
                labelStyle={{ width: "75px" }}
                selectStyle={{ width: "200px" }}
              />

              <span>{glassIcon(glassType as any)}</span>
            </div>

            <Input
              control={control}
              errors={errors}
              defaultValue={initialValues.notes}
              name="notes"
              label="Notes"
              multiline={true}
              numberOfLines={8}
              fieldRef={notesRef}
            />

            <Accordion type="single" collapsible>
              <AccordionItem value="additional-fields">
                <AccordionTrigger>Additional Fields</AccordionTrigger>
                <AccordionContent>
                  <HookTagInput
                    control={control}
                    name="tags"
                    label="Tags"
                    errors={errors}
                    defaultValue={initialValues.tags}
                  />
                  <Select
                    control={control}
                    errors={errors}
                    defaultValue={initialValues.sale_status}
                    name="sale_status"
                    label="Sale Status"
                    items={[
                      { label: "", value: "" },
                      { label: "For Sale", value: "For Sale" },
                      { label: "Not For Sale", value: "Not For Sale" },
                      { label: "Sold", value: "Sold" },
                      { label: "Destroyed", value: "Destroyed" },
                      { label: "Gifted", value: "Gifted" },
                    ]}
                  />
                  <Input
                    control={control}
                    errors={errors}
                    defaultValue={initialValues.material_cost}
                    name="material_cost"
                    label="Material Cost"
                    rules={{ required: false }}
                  />
                  <Input
                    control={control}
                    errors={errors}
                    defaultValue={initialValues.firing_cost}
                    name="firing_cost"
                    label="Firing Cost"
                    rules={{ required: false }}
                  />
                  <Input
                    control={control}
                    errors={errors}
                    defaultValue={initialValues.labour_cost}
                    name="labour_cost"
                    label="Labour Cost"
                    rules={{ required: false }}
                  />
                  <Input
                    control={control}
                    errors={errors}
                    defaultValue={initialValues.sale_price}
                    name="sale_price"
                    label="Sale Price"
                    rules={{ required: false }}
                  />
                  <Input
                    control={control}
                    errors={errors}
                    defaultValue={initialValues.sold_to}
                    name="sold_to"
                    label="Sold To"
                    multiline={true}
                    rules={{ required: false }}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
        <Button variant="outline" type="submit">Update</Button>
      </form>
    </>
  );
};

export default ProjectForm;
